import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Lock, ArrowLeft, Shield, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { createSubscriptionIntent, setPaymentSuccess, setPaymentError, setPaymentProcessing, resetPaymentState, fetchMySubscription } from '../redux/slices/paymentSlice';
import { fetchUser } from '../redux/slices/userSlice'; // You'll need to add this

// Load Stripe (replace with your publishable key)
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#0f172a',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: '500',
            '::placeholder': {
                color: '#94a3b8',
            },
        },
        invalid: {
            color: '#ef4444',
        },
    },
};

// Dynamic plan IDs will be fetched from Redux state

function PaymentForm() {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.user);
    const { clientSecret, isProcessing, error: paymentError, subscription, plans } = useSelector((state) => state.payment);

    const searchParams = new URLSearchParams(location.search);
    const initialBillingCycle = location.state?.billingCycle || searchParams.get('plan') || 'yearly';
    const [billingCycle] = useState(initialBillingCycle);

    const planName = location.state?.planName || 'Professional';
    const isSingleUse = planName === 'SingleUse' || planName === 'Single Use';
    
    // Calculate display price and total based on plan type
    let displayPrice = '49.99';

    if (isSingleUse) {
        displayPrice = '49.99';
    } else {
        const monthlyEquivalent = location.state?.price || (planName === 'Professional' ? (billingCycle === 'yearly' ? (2999.99 / 12).toFixed(2) : 299.99) : (billingCycle === 'yearly' ? (399.99 / 12).toFixed(2) : 39.99));
        const actualTotal = billingCycle === 'yearly'
            ? (planName === 'Professional' ? 2999.99 : 399.99)
            : parseFloat(monthlyEquivalent);
        displayPrice = billingCycle === 'yearly' ? actualTotal.toFixed(2) : parseFloat(monthlyEquivalent).toFixed(2);
    }

    const [formData, setFormData] = useState({
        email: user?.email || '',
        cardName: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
    });

    const [errors, setErrors] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [cardErrors, setCardErrors] = useState({});
    const [pollingStatus, setPollingStatus] = useState('');
    const [redirectTimer, setRedirectTimer] = useState(10);


    // Check if user already has active subscription
    useEffect(() => {
        dispatch(fetchMySubscription());
    }, [dispatch]);

    // Redirect if already subscribed (unless purchasing Single Use)
    useEffect(() => {
        if (subscription?.status === 'active' && !isSingleUse) {
            navigate('/');
        }
    }, [subscription, navigate, isSingleUse]);

    // Create subscription intent when component mounts
    useEffect(() => {
        if (!plans || plans.length === 0) return;

        let targetPlan;
        if (isSingleUse) {
            targetPlan = plans.find(p => p.name.includes('Single') || p.interval === 'one_time');
        } else {
            const targetInterval = billingCycle === 'yearly' ? 'year' : 'month';
            targetPlan = plans.find(p => p.interval === targetInterval && p.name.startsWith(planName));
        }

        if (targetPlan) {
            dispatch(createSubscriptionIntent({ planId: targetPlan.id }));
        }

        return () => {
            dispatch(resetPaymentState());
        };
    }, [billingCycle, dispatch, plans, planName, isSingleUse]);

    // Poll subscription status after payment
    const pollSubscriptionStatus = async (maxAttempts = 10) => {
        console.log('🔄 Starting subscription status polling...');

        for (let i = 0; i < maxAttempts; i++) {
            setPollingStatus(`Checking subscription status... (${i + 1}/${maxAttempts})`);

            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds

            try {
                const response = await dispatch(fetchMySubscription()).unwrap();

                console.log(`Attempt ${i + 1}: Status = ${response.status}`);

                if (response.status === 'active') {
                    console.log('✅ Subscription confirmed as active!');
                    setPollingStatus('Subscription activated successfully!');
                    return true;
                }

                if (response.status === 'incomplete') {
                    console.log('⏳ Subscription still incomplete, continuing to poll...');
                    continue;
                }

                // If status is something unexpected
                if (response.status !== 'inactive') {
                    console.warn(`⚠️ Unexpected status: ${response.status}`);
                }

            } catch (error) {
                console.error(`Error checking subscription (attempt ${i + 1}):`, error);
            }
        }

        console.warn('⚠️ Subscription not confirmed after polling');
        setPollingStatus('');
        return false;
    };

    // Auto-redirect timer effect
    useEffect(() => {
        let interval;
        if (showSuccessModal && redirectTimer > 0) {
            interval = setInterval(() => {
                setRedirectTimer((prev) => prev - 1);
            }, 1000);
        } else if (showSuccessModal && redirectTimer === 0) {
            navigate('/', { replace: true });
        }
        return () => clearInterval(interval);
    }, [showSuccessModal, redirectTimer, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleCardChange = (element, field) => (event) => {
        if (event.error) {
            setCardErrors(prev => ({ ...prev, [field]: event.error.message }));
        } else {
            setCardErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.email || !emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.cardName || formData.cardName.trim().length < 3) {
            newErrors.cardName = 'Please enter the name on card';
        }
        if (!formData.address || formData.address.trim().length < 5) {
            newErrors.address = 'Please enter your street address';
        }
        if (!formData.city || formData.city.trim().length < 2) {
            newErrors.city = 'Please enter your city';
        }
        if (!formData.state || formData.state.trim().length < 2) {
            newErrors.state = 'Please enter your state';
        }
        if (!formData.country) {
            newErrors.country = 'Please select a country';
        }
        if (!formData.postalCode || formData.postalCode.length < 3) {
            newErrors.postalCode = 'Valid postal code required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm() || !stripe || !elements || !clientSecret) {
            if (!clientSecret) {
                dispatch(setPaymentError('Payment system not initialized. Please refresh and try again.'));
            }
            return;
        }

        dispatch(setPaymentProcessing(true));

        try {
            const cardNumberElement = elements.getElement(CardNumberElement);

            console.log('💳 Confirming payment...');
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardNumberElement,
                    billing_details: {
                        name: formData.cardName,
                        email: formData.email,
                        address: {
                            line1: formData.address,
                            city: formData.city,
                            state: formData.state,
                            postal_code: formData.postalCode,
                            country: formData.country,
                        },
                    },
                },
            });

            if (error) {
                console.error('❌ Payment failed:', error);
                dispatch(setPaymentError(error.message));
                dispatch(setPaymentProcessing(false));
                setShowErrorModal(true);
            } else if (paymentIntent.status === 'succeeded') {
                console.log('✅ Payment succeeded! Payment Intent ID:', paymentIntent.id);
                dispatch(setPaymentSuccess('Payment successful! Activating your subscription...'));

                // Poll for subscription activation
                console.log('🔄 Waiting for webhook to activate subscription...');
                const isActive = await pollSubscriptionStatus();

                if (isActive) {
                    // Refresh user data to get updated subscription status
                    try {
                        await dispatch(fetchUser()).unwrap();
                        console.log('✅ User data refreshed with new subscription');
                    } catch (err) {
                        console.error('Error refreshing user data:', err);
                    }

                    dispatch(setPaymentProcessing(false));
                    dispatch(setPaymentSuccess('Subscription activated! Redirecting...'));

                    // Show success modal instead of direct navigation
                    setShowSuccessModal(true);
                } else {
                    dispatch(setPaymentProcessing(false));
                    dispatch(setPaymentError('Payment succeeded but subscription activation is taking longer than expected. Please refresh the page or contact support if the issue persists.'));
                    setShowErrorModal(true);
                }
            }
        } catch (err) {
            console.error('💥 Unexpected error during payment:', err);
            dispatch(setPaymentError('An unexpected error occurred. Please try again.'));
            dispatch(setPaymentProcessing(false));
            setShowErrorModal(true);
        }
    };

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 pt-24 pb-10 px-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-indigo-200/20 to-blue-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

                <div className="max-w-3xl mx-auto relative z-10">
                    <button
                        onClick={() => {
                            navigate('/');
                            setTimeout(() => {
                                const element = document.getElementById('subscription-plans');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }, 100);
                        }}
                        className="flex items-center px-3 py-4 gap-2 text-slate-600 bg-white/80 backdrop-blur-sm font-semibold text-sm rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-white shadow-sm hover:shadow-md transition-all mb-8 group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" strokeWidth={2.5} />
                        <span>Back to Plans</span>
                    </button>

                    {/* Error Alert */}
                    {paymentError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-red-800 text-sm font-medium">{paymentError}</p>
                        </div>
                    )}

                    {/* Polling Status */}
                    {pollingStatus && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-blue-800 text-sm font-medium">{pollingStatus}</p>
                        </div>
                    )}

                    <div className="bg-white/60 backdrop-blur-2xl rounded-3xl overflow-hidden shadow-2xl border border-white/50 ring-1 ring-black/5">
                        {/* Plan Header */}
                        <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 p-5 text-center overflow-hidden">
                            <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
                            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-400/20 rounded-full blur-2xl"></div>

                            <div className="relative z-10">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold text-white mb-3 border border-white/30">
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {isSingleUse ? 'SINGLE USE PLAN' : `${planName.toUpperCase()} PLAN`}
                                </div>
                                <div className="flex items-baseline justify-center gap-1.5 mb-1">
                                    <span className="text-5xl font-bold text-white drop-shadow-lg">${displayPrice}</span>
                                    <span className="text-2xl text-blue-100 font-semibold">{isSingleUse ? '/one-time' : (billingCycle === 'yearly' ? '/year' : '/month')}</span>
                                </div>
                                {isSingleUse ? (
                                    <p className="text-blue-100 text-sm font-medium mt-1">One-time single property analysis report</p>
                                ) : billingCycle === 'yearly' ? (
                                    <div className="mt-2 space-y-1.5">
                                        <p className="text-blue-100 text-sm font-medium">
                                            That's only <span className="font-bold text-white">${(parseFloat(displayPrice) / 12).toFixed(2)}/month</span>
                                        </p>
                                        <span className="inline-block px-3 py-1 bg-emerald-400/20 backdrop-blur-sm text-emerald-100 rounded-lg text-xs font-bold border border-emerald-300/30">
                                            💰 Save 20% with yearly billing
                                        </span>
                                    </div>
                                ) : (
                                    <p className="text-blue-100 text-sm font-medium mt-1">Billed monthly</p>
                                )}
                            </div>
                        </div>

                        {/* Form Section */}
                        <form onSubmit={handleSubmit} className="p-8 md:p-10">
                            <div className="space-y-8">
                                {/* Contact Information */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-transparent rounded-full"></div>
                                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Contact Information</h3>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2.5">Email Address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="you@example.com"
                                            className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="px-4 bg-white/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">Payment Details</span>
                                    </div>
                                </div>

                                {/* Payment Details */}
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2.5">Cardholder Name</label>
                                        <input
                                            type="text"
                                            name="cardName"
                                            value={formData.cardName}
                                            onChange={handleInputChange}
                                            placeholder="Enter name on card"
                                            className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.cardName ? 'border-red-400' : 'border-slate-200'}`}
                                        />
                                        {errors.cardName && (
                                            <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                {errors.cardName}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2.5">Card Number</label>
                                        <div className={`relative px-5 py-3 border-2 rounded-2xl bg-white/80 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all ${cardErrors.cardNumber ? 'border-red-400' : 'border-slate-200'}`}>
                                            <CardNumberElement
                                                options={CARD_ELEMENT_OPTIONS}
                                                onChange={handleCardChange(elements, 'cardNumber')}
                                            />
                                        </div>
                                        {cardErrors.cardNumber && (
                                            <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                {cardErrors.cardNumber}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">Expiry Date</label>
                                            <div className={`px-5 py-3 border-2 rounded-2xl bg-white/80 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all ${cardErrors.cardExpiry ? 'border-red-400' : 'border-slate-200'}`}>
                                                <CardExpiryElement
                                                    options={CARD_ELEMENT_OPTIONS}
                                                    onChange={handleCardChange(elements, 'cardExpiry')}
                                                />
                                            </div>
                                            {cardErrors.cardExpiry && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {cardErrors.cardExpiry}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">CVV</label>
                                            <div className={`px-5 py-3 border-2 rounded-2xl bg-white/80 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all ${cardErrors.cardCvc ? 'border-red-400' : 'border-slate-200'}`}>
                                                <CardCvcElement
                                                    options={CARD_ELEMENT_OPTIONS}
                                                    onChange={handleCardChange(elements, 'cardCvc')}
                                                />
                                            </div>
                                            {cardErrors.cardCvc && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {cardErrors.cardCvc}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-200"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="px-4 bg-white/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing Address</span>
                                    </div>
                                </div>

                                {/* Billing Address */}
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2.5">Street Address</label>
                                        <input
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="1234 Main St"
                                            className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.address ? 'border-red-400' : 'border-slate-200'}`}
                                        />
                                        {errors.address && (
                                            <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                {errors.address}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">City</label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                placeholder="City"
                                                className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.city ? 'border-red-400' : 'border-slate-200'}`}
                                            />
                                            {errors.city && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {errors.city}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">State / Province</label>
                                            <input
                                                type="text"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleInputChange}
                                                placeholder="State"
                                                className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.state ? 'border-red-400' : 'border-slate-200'}`}
                                            />
                                            {errors.state && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {errors.state}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">Country</label>
                                            <select
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all appearance-none ${errors.country ? 'border-red-400' : 'border-slate-200'}`}
                                            >
                                                <option value="">Select Country</option>
                                                <option value="US">United States</option>
                                                <option value="IN">India</option>
                                                <option value="GB">United Kingdom</option>
                                                <option value="CA">Canada</option>
                                                <option value="AU">Australia</option>
                                                <option value="DE">Germany</option>
                                                <option value="FR">France</option>
                                            </select>
                                            {errors.country && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {errors.country}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2.5">Postal Code</label>
                                            <input
                                                type="text"
                                                name="postalCode"
                                                value={formData.postalCode}
                                                onChange={handleInputChange}
                                                placeholder="Zip Code"
                                                className={`w-full px-5 py-3 border-2 rounded-2xl font-medium text-slate-900 bg-white/80 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all ${errors.postalCode ? 'border-red-400' : 'border-slate-200'}`}
                                            />
                                            {errors.postalCode && (
                                                <p className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                                    {errors.postalCode}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isProcessing || !stripe || !clientSecret}
                                        className="group relative w-full py-5 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700 text-white text-base font-bold rounded-2xl transition-all duration-300 shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                                        {isProcessing ? (
                                            <div className="relative flex items-center justify-center gap-3">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Processing Payment...</span>
                                            </div>
                                        ) : (
                                            <div className="relative flex items-center justify-center gap-3">
                                                <Lock className="w-5 h-5" />
                                                <span>Pay ${displayPrice} {isSingleUse ? 'One-Time' : (billingCycle === 'yearly' ? 'Yearly' : 'Monthly')} Securely</span>
                                            </div>
                                        )}
                                    </button>

                                    <p className="text-center text-xs text-slate-400 mt-4">
                                        Powered by <span className="font-bold text-slate-600">Stripe</span>
                                    </p>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Error Modal */}
                {showErrorModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => setShowErrorModal(false)}></div>
                        <div className="relative bg-white rounded-3xl p-12 max-w-md w-full shadow-2xl animate-scale-in">
                            <button
                                onClick={() => setShowErrorModal(false)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <div className="text-center">
                                <div className="mx-auto mb-6 w-24 h-24 bg-red-50 rounded-full flex items-center justify-center border-4 border-red-100">
                                    <AlertCircle className="w-12 h-12 text-red-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">Payment Failed</h3>
                                <p className="text-slate-600 text-base mb-8 leading-relaxed">
                                    {paymentError || 'We couldn\'t process your payment. Please check your card details and try again.'}
                                </p>
                                <button
                                    onClick={() => setShowErrorModal(false)}
                                    className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-colors shadow-lg shadow-red-200"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Modal */}
                {showSuccessModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md"></div>
                        <div className="relative bg-white rounded-3xl p-12 max-w-md w-full shadow-2xl animate-scale-in">
                            <div className="text-center">
                                <div className="mx-auto mb-6 w-24 h-24 bg-green-50 rounded-full flex items-center justify-center border-4 border-green-100">
                                    <CheckCircle className="w-12 h-12 text-green-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-3">Subscription Active!</h3>
                                <p className="text-slate-600 text-base mb-4 leading-relaxed">
                                    Your payment was successful and your {planName} features are now unlocked. Welcome aboard!
                                </p>
                                <p className="text-sm text-slate-500 mb-8">
                                    Redirecting automatically in <span className="font-bold text-slate-700">{redirectTimer}</span> seconds...
                                </p>
                                <button
                                    onClick={() => navigate('/', { replace: true })}
                                    className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-colors shadow-lg shadow-green-200"
                                >
                                    Go to Dashboard Now
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}

export default function Payment() {
    if (!stripePromise) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
                    <div className="text-center p-8 bg-white rounded-2xl shadow-xl border border-red-100 max-w-md">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Shield className="w-8 h-8 text-red-500" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800 mb-2">Configuration Error</h1>
                        <p className="text-slate-600 mb-6">The payment system is currently unavailable due to missing configuration (Stripe Publishable Key).</p>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="px-6 py-2 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 transition-colors"
                        >
                            Return Home
                        </button>
                    </div>
                </div>
            </>
        );
    }
    return (
        <Elements stripe={stripePromise}>
            <PaymentForm />
        </Elements>
    );
}